import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Box, Button, Divider, FormControlLabel, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';

import { BearingEnvelope } from '@paragon/contract-library';
import { point2DEquals } from '@paragon/geometry-library';
import { OptionalTooltip } from '@paragon/material';

import { getBearingEnvelopeUserVisibleTerm } from '../../../../library/utilities/bearingEnvelopeUserTerm';
import { cancelInsertion } from '../../../../store/actions/insertActions';
import { finishInsertingBearingEnvelope } from '../../../../store/actions/layout/bearingEnvelopeActions';
import { getInsertingBearingEnvelopeSettingsOrNull } from '../../../../store/selectors/bearingEnvelopeInsertionSelectors';
import {
  bearingEnvelopeElevationsMatch,
  getActiveLevelOrNull,
  getBearingEnvelopeElevationsFromLevel,
} from '../../../../store/selectors/layoutDataSelectors';
import { updateInsertingBearingEnvelope } from '../../../../store/slices/applicationSlice';
import { layoutSlice } from '../../../../store/slices/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { EnvelopeJustificationToggle } from '../elements/EnvelopeJustificationToggle';
import { BearingEnvelopeCommonProperties } from '../properties/roof-planes/bearing-envelopes/BearingEnvelopeCommonProperties';
import { BearingEnvelopeEasyModeProperties } from '../properties/roof-planes/bearing-envelopes/easy/BearingEnvelopeEasyModeProperties';
import {
  flipBearingEnvelope,
  FlippableBearingEnvelopeProperties,
} from '../properties/roof-planes/bearing-envelopes/flipBearingEnvelope';
import { InsertAutomaticallySwitch } from './InsertAutomaticallySwitch';
import InsertionPanelContainer from './InsertionPanelContainer';
import { LayoutPointPickerField } from './truss-envelope/LayoutPointPickerField';

const ROOF_OPTION_LABEL_STYLE = { m: 0, flexShrink: 0 } as const;

export function BearingEnvelopeInsertionSubPanel() {
  const dispatch = useAppDispatch();

  const insertingObjectInfo = useAppSelector((state) => state.application.insertingObjectInfo);
  const bearingEnvelopeInsertionSettings = useAppSelector(getInsertingBearingEnvelopeSettingsOrNull);
  const roofPlaneInsertionSettings = useAppSelector((state) => state.layout.roofPlaneInsertionSettings);
  const activeLevel = useAppSelector(getActiveLevelOrNull);

  if (insertingObjectInfo?.type !== 'bearingEnvelope' || bearingEnvelopeInsertionSettings == null) return null;

  const insertionEnvelope = insertingObjectInfo.data;

  function saveChangedProperties(changes: Partial<Omit<BearingEnvelope, 'guid' | 'name' | 'bearingType'>>) {
    dispatch(updateInsertingBearingEnvelope({ bearingType: insertionEnvelope.bearingType, changes }));

    // if elevations come back into sync with the active level, record that so future insertions follow suit

    const changeAffectsLevelRelativeElevations =
      changes.top != null || changes.bottom != null || changes.beamOrientation != null;

    if (changeAffectsLevelRelativeElevations && activeLevel != null) {
      const nextEnvelope = { ...insertionEnvelope, ...changes };
      const nextElevationsFromLevel = getBearingEnvelopeElevationsFromLevel(activeLevel, nextEnvelope);

      if (bearingEnvelopeElevationsMatch(nextElevationsFromLevel, nextEnvelope)) {
        dispatch(
          layoutSlice.actions.insertionBearingEnvelopeElevationsSynchronizedWithLevel({
            bearingType: insertionEnvelope.bearingType,
          }),
        );
      }
    }
  }

  const { leftPoint, rightPoint } = insertionEnvelope;
  const bothPointsDefined = leftPoint != null && rightPoint != null;
  const disabled = !bothPointsDefined || point2DEquals(leftPoint, rightPoint);

  const bearingTerm = getBearingEnvelopeUserVisibleTerm(insertionEnvelope.bearingType);
  const bearingTermLowercase = getBearingEnvelopeUserVisibleTerm(insertionEnvelope.bearingType, { capitalize: false });
  const bearingTermPluralLowercase = getBearingEnvelopeUserVisibleTerm(insertionEnvelope.bearingType, {
    plural: true,
    capitalize: false,
  });

  return (
    <InsertionPanelContainer
      sx={{ padding: 2, marginBottom: 0, display: 'flex', flexDirection: 'column' }}
      disabled={disabled}
      onInsert={async () => {
        await dispatch(finishInsertingBearingEnvelope({ postInsertionAction: 'repeat' }));
      }}
      additionalButtons={
        <InsertAutomaticallySwitch
          checked={bearingEnvelopeInsertionSettings.autoInsertOnSecondPoint}
          canInsert={!disabled}
          insertedObjectName={bearingTermLowercase}
          onChange={(checked) => {
            dispatch(
              layoutSlice.actions.bearingEnvelopeInsertionAutoInsertOnSecondPointSet({
                bearingType: insertionEnvelope.bearingType,
                autoInsertOnSecondPoint: checked,
              }),
            );
          }}
          onInsert={async () => {
            await dispatch(finishInsertingBearingEnvelope({ postInsertionAction: 'repeat' }));
          }}
        />
      }
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 2 }}>
        <Typography align="center" variant="h5" sx={{ marginBottom: 3 }}>
          {bearingTerm} Creation
        </Typography>

        <Tooltip disableInteractive title="Close insertion panel (Esc)">
          <IconButton
            aria-label="Cancel Insertion"
            onClick={() => {
              dispatch(cancelInsertion());
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={2}>
        <BearingEnvelopeCommonProperties properties={insertionEnvelope} onChange={saveChangedProperties} />

        <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1, rowGap: 1, flexWrap: 'wrap' }}>
          <Tooltip disableInteractive title={`Create a roof plane with each ${bearingTermLowercase}`}>
            <FormControlLabel
              sx={ROOF_OPTION_LABEL_STYLE}
              label="Include Roof Plane"
              control={
                <Switch
                  checked={bearingEnvelopeInsertionSettings.insertRoofPlane}
                  onChange={(event) =>
                    dispatch(
                      layoutSlice.actions.bearingEnvelopeInsertionRoofPlaneInsertionModeSet({
                        bearingType: insertionEnvelope.bearingType,
                        insertRoofPlane: event.target.checked,
                      }),
                    )
                  }
                />
              }
            />
          </Tooltip>

          <OptionalTooltip
            visible={bearingEnvelopeInsertionSettings.insertRoofPlane}
            disableInteractive
            title={`Auto-cut added roof planes when done inserting ${bearingTermPluralLowercase}`}
          >
            <FormControlLabel
              disabled={!bearingEnvelopeInsertionSettings.insertRoofPlane}
              sx={ROOF_OPTION_LABEL_STYLE}
              label="Auto-Cut"
              control={
                <Switch
                  checked={roofPlaneInsertionSettings.autoCutOnInsert}
                  onChange={(event) => dispatch(layoutSlice.actions.roofPlaneInsertionAutoCutSet(event.target.checked))}
                />
              }
            />
          </OptionalTooltip>
        </Stack>

        <Box>
          <EnvelopeJustificationToggle
            value={insertionEnvelope.justification}
            onChange={(justification) => {
              saveChangedProperties({ justification });
            }}
          />
        </Box>

        <Divider />

        <LayoutPointPickerField
          label="Start Point"
          target="bearing-envelope-left"
          value={insertionEnvelope.leftPoint}
          allowSwitchWhilePicking
        />

        <OptionalTooltip visible={leftPoint == null} title="Available once start point chosen">
          <LayoutPointPickerField
            label="End Point"
            target="bearing-envelope-right"
            value={insertionEnvelope.rightPoint}
            pending={leftPoint == null}
            allowSwitchWhilePicking={leftPoint != null}
          />
        </OptionalTooltip>

        <Tooltip disableInteractive title={`Flip ${bearingTerm}`}>
          <Box component="span" sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              fullWidth
              startIcon={<SwapVertIcon />}
              disabled={!bothPointsDefined}
              onClick={() => {
                saveChangedProperties(flipBearingEnvelope(insertionEnvelope as FlippableBearingEnvelopeProperties));
              }}
            >
              Flip {bearingTerm}
            </Button>
          </Box>
        </Tooltip>

        <Divider />

        <BearingEnvelopeEasyModeProperties
          properties={insertionEnvelope}
          bearingEnvelopeInsertionSettings={bearingEnvelopeInsertionSettings}
          onChange={saveChangedProperties}
        />
      </Stack>
    </InsertionPanelContainer>
  );
}
